import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import {
    FaArrowDown,
    FaArrowUp,
    FaBriefcase,
    FaChartPie,
    FaClock,
    FaDownload,
    FaFileAlt,
    FaGlobeAmericas,
    FaInbox,
    FaStar,
    FaUsers,
    FaUserTie
} from 'react-icons/fa';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Line,
    LineChart,
    Pie,
    PieChart,
    RadialBar,
    RadialBarChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';

// Custom tooltip component for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-4 rounded-xl border border-luxury-200 shadow-lg">
        <p className="text-sm font-medium text-luxury-900 mb-1">{label}</p>
        {payload.map((entry, index) => (
          <div key={`item-${index}`} className="flex items-center">
            <div 
              className="h-2 w-2 rounded-full mr-2" 
              style={{ backgroundColor: entry.color }}
            />
            <p className="text-sm text-luxury-700">
              {entry.name}: <span className="text-luxury-900 font-medium">{entry.value}</span>
            </p>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalRecruiters: 0,
    totalJobs: 0,
    totalApplications: 0,
    activeJobs: 0,
    pendingApplications: 0,
    bulkRequestsPending: 0,
    monthlyGrowth: 0
  });
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState('7d');
  const { apiCall } = useAuth();

  useEffect(() => {
    fetchDashboardData();
  }, [selectedTimeRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await apiCall('GET', `/api/admin/dashboard?timeRange=${selectedTimeRange}`);
      if (response.success) {
        setStats(response.data.stats);
        setChartData(response.data.chartData);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      change: '+12%',
      trend: 'up',
      icon: FaUsers,
      color: 'primary',
      bgGradient: 'from-primary-400 to-primary-600',
      shadowColor: 'shadow-primary-500/20'
    },
    {
      title: 'Total Recruiters',
      value: stats.totalRecruiters,
      change: '+8%',
      trend: 'up',
      icon: FaUserTie,
      color: 'success',
      bgGradient: 'from-success-400 to-success-600',
      shadowColor: 'shadow-success-500/20'
    },
    {
      title: 'Active Jobs',
      value: stats.activeJobs,
      change: '+15%',
      trend: 'up',
      icon: FaBriefcase,
      color: 'warning',
      bgGradient: 'from-warning-400 to-warning-600',
      shadowColor: 'shadow-warning-500/20'
    },
    {
      title: 'Applications',
      value: stats.totalApplications,
      change: '+23%',
      trend: 'up',
      icon: FaFileAlt,
      color: 'danger',
      bgGradient: 'from-danger-400 to-danger-600',
      shadowColor: 'shadow-danger-500/20'
    }
  ];

  const recentActivity = [
    { id: 1, type: 'user', action: 'New user registered', user: 'John Doe', time: '2 minutes ago' },
    { id: 2, type: 'job', action: 'Job posted', user: 'TechCorp Inc.', time: '15 minutes ago' },
    { id: 3, type: 'application', action: 'Application submitted', user: 'Jane Smith', time: '1 hour ago' },
    { id: 4, type: 'recruiter', action: 'Recruiter approved', user: 'Global Tech Solutions', time: '2 hours ago' },
    { id: 5, type: 'bulk', action: 'Bulk request submitted', user: 'StartupHub', time: '3 hours ago' }
  ];

  const pieData = [
    { name: 'Active Users', value: 65, color: '#0ea5e9' },
    { name: 'Pending Applications', value: 25, color: '#f59e0b' },
    { name: 'Completed', value: 10, color: '#10b981' }
  ];

  const monthlyData = [
    { month: 'Jan', users: 120, jobs: 45, applications: 89 },
    { month: 'Feb', users: 145, jobs: 52, applications: 112 },
    { month: 'Mar', users: 189, jobs: 61, applications: 145 },
    { month: 'Apr', users: 210, jobs: 78, applications: 167 },
    { month: 'May', users: 245, jobs: 89, applications: 201 },
    { month: 'Jun', users: 289, jobs: 95, applications: 234 }
  ];

  const radialData = [
    { name: 'Users', value: 78, fill: '#0ea5e9' },
    { name: 'Jobs', value: 65, fill: '#f59e0b' },
    { name: 'Applications', value: 91, fill: '#10b981' },
    { name: 'Recruiters', value: 42, fill: '#6366f1' }
  ];

  const categoryData = [
    { name: 'Tech', value: 35, color: '#0ea5e9' },
    { name: 'Marketing', value: 25, color: '#f59e0b' },
    { name: 'Sales', value: 20, color: '#10b981' },
    { name: 'Finance', value: 15, color: '#6366f1' },
    { name: 'Other', value: 5, color: '#8b5cf6' },
  ];

  const locationData = [
    { name: 'New York', value: 30 },
    { name: 'San Francisco', value: 25 },
    { name: 'London', value: 15 },
    { name: 'Berlin', value: 12 },
    { name: 'Singapore', value: 10 },
    { name: 'Other', value: 8 },
  ];

  // Animation variants for staggered animation
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <motion.div 
          initial={{ rotate: 0 }}
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600"
        ></motion.div>
      </div>
    );
  }

  return (
    <motion.div 
      initial="hidden"
      animate="show"
      variants={containerVariants}
      className="space-y-8"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-luxury-900 tracking-tight">Dashboard</h1>
          <p className="text-luxury-600 mt-1">Welcome back! Here's what's happening with jobzentro.</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            className="px-4 py-2 bg-white border border-luxury-200 rounded-xl text-sm font-medium text-luxury-800 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 shadow-sm hover:shadow"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="luxury-button-primary text-sm flex items-center"
          >
            <FaDownload className="mr-2" />
            Export
          </motion.button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div 
        variants={containerVariants}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {statCards.map((card, index) => (
          <motion.div
            key={card.title}
            variants={itemVariants}
            whileHover={{ 
              y: -5,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
            className={`bg-white rounded-2xl shadow-lg ${card.shadowColor} border border-luxury-200/50 p-6 transition-all duration-500 hover:border-${card.color}-400/50`}
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-luxury-600">{card.title}</p>
                <h3 className="text-2xl font-bold text-luxury-900 mt-2">
                  {card.value.toLocaleString()}
                </h3>
                <div className="flex items-center mt-2.5">
                  {card.trend === 'up' ? (
                    <FaArrowUp className="text-success-500 text-xs mr-1" />
                  ) : (
                    <FaArrowDown className="text-danger-500 text-xs mr-1" />
                  )}
                  <span className={`text-sm font-medium ${
                    card.trend === 'up' ? 'text-success-600' : 'text-danger-600'
                  }`}>
                    {card.change}
                  </span>
                  <span className="text-luxury-500 text-xs ml-1">vs last period</span>
                </div>
              </div>
              <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${card.bgGradient} flex items-center justify-center shadow-lg`}>
                <card.icon className="h-5 w-5 text-white" />
              </div>
            </div>
            
            {/* Mini sparkline chart could go here */}
            <div className="h-10 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyData.slice(0, 6)} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <Line 
                    type="monotone" 
                    dataKey={index === 0 ? "users" : index === 1 ? "recruiters" : index === 2 ? "jobs" : "applications"} 
                    stroke={index === 0 ? "#0ea5e9" : index === 1 ? "#22c55e" : index === 2 ? "#f59e0b" : "#ef4444"} 
                    strokeWidth={2} 
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Area Chart */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-2xl border border-luxury-200/50 shadow-lg p-6 lg:col-span-2"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-luxury-900">Growth Overview</h3>
              <p className="text-sm text-luxury-600">Monthly statistics</p>
            </div>
            <div className="flex space-x-3">
              <motion.div whileHover={{ scale: 1.1 }} className="flex items-center space-x-2">
                <div className="h-3 w-3 rounded-full bg-primary-500"></div>
                <span className="text-xs text-luxury-600">Users</span>
              </motion.div>
              <motion.div whileHover={{ scale: 1.1 }} className="flex items-center space-x-2">
                <div className="h-3 w-3 rounded-full bg-success-500"></div>
                <span className="text-xs text-luxury-600">Jobs</span>
              </motion.div>
              <motion.div whileHover={{ scale: 1.1 }} className="flex items-center space-x-2">
                <div className="h-3 w-3 rounded-full bg-warning-500"></div>
                <span className="text-xs text-luxury-600">Applications</span>
              </motion.div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={monthlyData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorJobs" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorApplications" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <XAxis 
                dataKey="month" 
                axisLine={false} 
                tickLine={false} 
                tick={{ fill: '#78716c', fontSize: 12 }}
              />
              <YAxis 
                axisLine={false} 
                tickLine={false} 
                tick={{ fill: '#78716c', fontSize: 12 }}
              />
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e5e4" />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="users" 
                stroke="#0ea5e9" 
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#colorUsers)" 
              />
              <Area 
                type="monotone" 
                dataKey="jobs" 
                stroke="#22c55e" 
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#colorJobs)" 
              />
              <Area 
                type="monotone" 
                dataKey="applications" 
                stroke="#f59e0b"
                strokeWidth={2} 
                fillOpacity={1} 
                fill="url(#colorApplications)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Pie Chart */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-2xl border border-luxury-200/50 shadow-lg p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-luxury-900">Job Categories</h3>
              <p className="text-sm text-luxury-600">Distribution by sector</p>
            </div>
            <FaChartPie className="text-luxury-500" />
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={4}
                dataKey="value"
                startAngle={90}
                endAngle={-270}
                stroke="none"
              >
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {categoryData.map((item, index) => (
              <motion.div 
                key={`legend-${index}`}
                whileHover={{ scale: 1.05 }}
                className="flex items-center space-x-2"
              >
                <div 
                  className="h-3 w-3 rounded-full" 
                  style={{ backgroundColor: item.color }}
                ></div>
                <span className="text-xs text-luxury-700">{item.name} ({item.value}%)</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Second Row Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bar Chart */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-2xl border border-luxury-200/50 shadow-lg p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-luxury-900">Job Locations</h3>
              <p className="text-sm text-luxury-600">Top hiring cities</p>
            </div>
            <FaGlobeAmericas className="text-luxury-500" />
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart 
              data={locationData}
              layout="vertical"
              margin={{ top: 0, right: 0, left: 20, bottom: 0 }}
            >
              <XAxis 
                type="number" 
                hide 
              />
              <YAxis 
                dataKey="name" 
                type="category" 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#78716c', fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="value" 
                radius={[0, 4, 4, 0]}
              >
                {locationData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={`hsl(${210 + index * 25}, 70%, 55%)`} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Radial Chart */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-2xl border border-luxury-200/50 shadow-lg p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-luxury-900">Performance</h3>
              <p className="text-sm text-luxury-600">Goal completion</p>
            </div>
            <FaStar className="text-luxury-500" />
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <RadialBarChart 
              cx="50%" 
              cy="50%" 
              innerRadius="15%" 
              outerRadius="80%" 
              data={radialData} 
              startAngle={90} 
              endAngle={-270}
            >
              <RadialBar
                minAngle={15}
                background
                clockWise
                dataKey="value"
                cornerRadius={10}
              />
              <Tooltip content={<CustomTooltip />} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="flex justify-around mt-2">
            {radialData.map((entry, index) => (
              <motion.div 
                key={`radial-${index}`}
                whileHover={{ scale: 1.05 }}
                className="flex flex-col items-center"
              >
                <div className="flex items-center space-x-1">
                  <div 
                    className="h-2.5 w-2.5 rounded-full" 
                    style={{ backgroundColor: entry.fill }}
                  ></div>
                  <span className="text-xs font-medium text-luxury-800">{entry.name}</span>
                </div>
                <span className="text-xs text-luxury-600">{entry.value}%</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-2xl border border-luxury-200/50 shadow-lg p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-luxury-900">Recent Activity</h3>
              <p className="text-sm text-luxury-600">Latest updates</p>
            </div>
            <FaClock className="text-luxury-500" />
          </div>
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start space-x-3 border-b border-luxury-100 pb-3 last:border-b-0"
              >
                <div className={`p-2 rounded-xl ${
                  activity.type === 'user' ? 'bg-primary-100 text-primary-600' :
                  activity.type === 'recruiter' ? 'bg-success-100 text-success-600' :
                  activity.type === 'job' ? 'bg-warning-100 text-warning-600' :
                  activity.type === 'application' ? 'bg-danger-100 text-danger-600' :
                  'bg-luxury-100 text-luxury-600'
                }`}>
                  {activity.type === 'user' && <FaUsers className="h-4 w-4" />}
                  {activity.type === 'recruiter' && <FaUserTie className="h-4 w-4" />}
                  {activity.type === 'job' && <FaBriefcase className="h-4 w-4" />}
                  {activity.type === 'application' && <FaFileAlt className="h-4 w-4" />}
                  {activity.type === 'bulk' && <FaInbox className="h-4 w-4" />}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <p className="text-sm font-medium text-luxury-900">{activity.action}</p>
                    <span className="text-xs text-luxury-500">{activity.time}</span>
                  </div>
                  <p className="text-xs text-luxury-600 mt-1">{activity.user}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Dashboard; 